/**
 * What the report draws of each bradycardia episode, and therefore the only
 * signal it needs to fetch (project_management#561).
 *
 * The chart list shows the three valid episodes with the lowest average heart
 * rate. Each `BradycardiaChart` cuts its excerpt (episode ± 1000 samples) into
 * 2,000-sample chunks and draws only the chunks around the slowest 30 s (plus
 * the last chunk when it holds the episode end). The backend used to send every
 * episode's whole excerpt — 138 MB for a 16 h staging sequence, up to 634k
 * samples per episode — to draw at most 3 × 4 chunks. The page now computes
 * this plan from the table query, asks `Analysis.excerptSlices` for exactly
 * those chunks, and rebuilds each excerpt as a full-length array that holds the
 * drawn chunks and nulls elsewhere: every drawn position carries the value the
 * whole excerpt carried, so the charts are pixel-identical.
 *
 * `BradycardiaChart` draws from the same `bradycardiaChunkPlan`, so what is
 * fetched and what is drawn cannot drift apart.
 */
import { RecordingJson } from 'types/schema.type';

/** `chunkArray`'s default chunk size, which `BradycardiaChart` uses. */
export const BRADYCARDIA_CHUNK_SIZE = 2000;

/** Samples of padding on each side of an episode excerpt (backend `getSignalExcerpt`). */
export const EXCERPT_PAD_SAMPLES = 1000;

/** How many episodes the report charts. */
export const BRADYCARDIA_CHART_EPISODES = 3;

type ChartableEpisode = { isValid?: boolean | null; avgBeat: number };

/**
 * The report's three charted episodes: valid ones, lowest average rate first.
 * Exactly the selection `BradycardiaChartList` made before #561 (it also
 * required a non-empty excerpt, which every episode had — the backend excerpt
 * of an episode is never empty).
 */
export function selectBradycardiaChartEpisodes<T extends ChartableEpisode>(episodes: T[]): T[] {
  const validEpisodes = episodes.filter((ep) => ep.isValid);

  return validEpisodes
    .sort((a, b) => a.avgBeat - b.avgBeat)
    .slice(0, BRADYCARDIA_CHART_EPISODES);
}

export type ChunkPlanInput = {
  /** Length of the excerpt (`signal.length`). */
  length: number;
  startIndex: number;
  endIndex: number;
  smallestAverageIndex: number;
};

export type ChunkPlan = {
  chunkCount: number;
  /** Drawn chunk index → whether it is the first renderable one (it carries the title). */
  drawn: Map<number, { isFirstRenderable: boolean }>;
};

/**
 * Which chunks `BradycardiaChart` draws. A verbatim port of its pre-#561
 * render loop, in arithmetic instead of materialised arrays: chunk `k` of the
 * x axis holds sample indexes `startIndex - 1000 + 2000k …` up to the excerpt
 * end. Pinned against the old loop by a randomized test.
 */
export function bradycardiaChunkPlan({
  length,
  startIndex,
  endIndex,
  smallestAverageIndex,
}: ChunkPlanInput): ChunkPlan {
  const chunkCount = Math.ceil(length / BRADYCARDIA_CHUNK_SIZE);
  const firstX = (k: number) => startIndex - EXCERPT_PAD_SAMPLES + k * BRADYCARDIA_CHUNK_SIZE;
  const lastX = (k: number) =>
    startIndex - EXCERPT_PAD_SAMPLES + Math.min((k + 1) * BRADYCARDIA_CHUNK_SIZE, length) - 1;

  // xAxisChunks.findIndex((chunk) => s >= chunk[0] && s <= chunk[chunk.length - 1])
  let smallestChunkIndex = -1;

  for (let k = 0; k < chunkCount; k += 1) {
    if (smallestAverageIndex >= firstX(k) && smallestAverageIndex <= lastX(k)) {
      smallestChunkIndex = k;
      break;
    }
  }

  // xAxisChunks[last].includes(endIndex): the chunk holds firstX, firstX + 1, …, lastX.
  const last = chunkCount - 1;
  const lastChunkHasEnd =
    chunkCount > 0 &&
    typeof endIndex === 'number' &&
    Number.isInteger(endIndex - firstX(last)) &&
    endIndex >= firstX(last) &&
    endIndex <= lastX(last);

  const drawn = new Map<number, { isFirstRenderable: boolean }>();
  let firstRenderableChunkFound = false;

  for (let index = 0; index < chunkCount; index += 1) {
    const shouldRender =
      (smallestChunkIndex === 0 && index < 3) ||
      (smallestChunkIndex === last && index >= chunkCount - 3) ||
      index === smallestChunkIndex ||
      index === smallestChunkIndex - 1 ||
      index === smallestChunkIndex + 1 ||
      (index === last && lastChunkHasEnd);

    if (shouldRender) {
      const isFirstRenderable = !firstRenderableChunkFound;

      firstRenderableChunkFound = true;

      // The last chunk is skipped when it does not hold the episode end — but
      // it has already claimed "first renderable" (pre-#561 behaviour).
      if (!(index === last && !lastChunkHasEnd)) {
        drawn.set(index, { isFirstRenderable });
      }
    }
  }

  return { chunkCount, drawn };
}

export type SliceRange = { from: number; to: number };

/** Drawn chunks as `[from, to)` excerpt positions, adjacent chunks merged. */
export function drawnRanges(plan: ChunkPlan, length: number): SliceRange[] {
  const ranges: SliceRange[] = [];

  Array.from(plan.drawn.keys())
    .sort((a, b) => a - b)
    .forEach((index) => {
      const from = index * BRADYCARDIA_CHUNK_SIZE;
      const to = Math.min(from + BRADYCARDIA_CHUNK_SIZE, length);
      const previous = ranges[ranges.length - 1];

      if (previous && previous.to === from) {
        previous.to = to;
      } else {
        ranges.push({ from, to });
      }
    });

  return ranges;
}

type PlannedEpisode = {
  startIndex: number;
  endIndex: number;
  lowestAvgBeatIndex: number;
};

export type ExcerptSlicesRequest = { startIndex: number; endIndex: number; slices: SliceRange[] };

/**
 * The `excerptSlices` request for one charted episode. The excerpt length is
 * the backend's (`end - start + 2000`); the backend answers the true length,
 * which differs only for an episode wholly past the signal end — an all-null
 * excerpt, which draws the same with or without these slices.
 */
export function bradycardiaExcerptRequest(episode: PlannedEpisode): ExcerptSlicesRequest {
  const length = episode.endIndex - episode.startIndex + 2 * EXCERPT_PAD_SAMPLES;
  const plan = bradycardiaChunkPlan({
    length,
    startIndex: episode.startIndex,
    endIndex: episode.endIndex,
    smallestAverageIndex: episode.lowestAvgBeatIndex,
  });

  return {
    startIndex: episode.startIndex,
    endIndex: episode.endIndex,
    slices: drawnRanges(plan, length),
  };
}

export type ExcerptSlicesResult = {
  startIndex: number;
  endIndex: number;
  length: number;
  slices: { from: number; to: number; ch1: (number | null)[] }[];
};

/** The excerpt as the chart reads it: `length` positions, the slices in place, null elsewhere. */
export function rebuildExcerpt(result: ExcerptSlicesResult): RecordingJson {
  const ch1 = new Array<number | null>(result.length).fill(null);

  result.slices.forEach(({ from, ch1: values }) => {
    values.forEach((value, i) => {
      ch1[from + i] = value;
    });
  });

  return { ch1 } as RecordingJson;
}

/**
 * The charted episodes with their rebuilt excerpts, matched by span. An
 * episode without a matching result keeps no excerpt and is not charted —
 * as before, when the chart query failed.
 */
export function withRebuiltExcerpts<T extends { startIndex: number; endIndex: number }>(
  episodes: T[],
  results: ExcerptSlicesResult[] | null | undefined,
): (T & { excerpt?: RecordingJson })[] {
  return episodes.map((episode) => {
    const result = results?.find(
      (r) => r.startIndex === episode.startIndex && r.endIndex === episode.endIndex,
    );

    return result ? { ...episode, excerpt: rebuildExcerpt(result) } : episode;
  });
}
